<script>
  import { Download, FileText, Paperclip } from 'lucide-svelte';
  import { normalizeUrl } from '$lib/utils';

  export let url = '';
  export let name = '';
  export let mime = '';
  export let size = '';
  export let onOpen = null;

  $: fileUrl = normalizeUrl(url);
  $: fileName = getFileName(name || fileUrl);
  $: typeLabel = getTypeLabel(mime, fileName);
  $: sizeLabel = formatSize(size);
  $: metaText = [typeLabel, sizeLabel].filter(Boolean).join(' · ');

  function getFileName(value) {
    const fallback = 'Tệp đính kèm';
    const raw = String(value || '').trim();
    if (!raw) return fallback;

    try {
      const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      return safeDecode(parsed.searchParams.get('name') || parsed.pathname.split('/').filter(Boolean).pop() || fallback);
    } catch {
      return safeDecode(raw.split(/[?#]/, 1)[0].split('/').filter(Boolean).pop() || fallback);
    }
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  function getTypeLabel(value, fallbackName) {
    const normalizedMime = String(value || '').trim().toLowerCase();
    if (normalizedMime) return normalizedMime.split('/').pop()?.toUpperCase() || 'FILE';

    const extension = String(fallbackName || '').split('.').pop();
    return extension && extension !== fallbackName ? extension.toUpperCase() : 'FILE';
  }

  function formatSize(value) {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value;

    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    let sizeValue = bytes;
    let unitIndex = 0;

    while (sizeValue >= 1024 && unitIndex < units.length - 1) {
      sizeValue /= 1024;
      unitIndex += 1;
    }

    return `${sizeValue.toFixed(sizeValue >= 10 || unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
  }
</script>

<section class="file-preview" aria-label={`Tệp: ${fileName}`}>
  <span class="file-icon" aria-hidden="true">
    <FileText size={20} strokeWidth={2} />
  </span>

  {#if onOpen}
    <button type="button" class="file-body" title={fileName} on:click|stopPropagation={onOpen}>
      <strong>{fileName}</strong>
      <span><Paperclip size={13} strokeWidth={2} />{metaText || 'Tệp đính kèm'}</span>
    </button>
  {:else}
  <a class="file-body" href={fileUrl} target="_blank" rel="noopener noreferrer" title={fileName}>
    <strong>{fileName}</strong>
    <span>
      <Paperclip size={13} strokeWidth={2} />
      {metaText || 'Tệp đính kèm'}
    </span>
  </a>
  {/if}

  {#if fileUrl}
    <a class="download-action" href={fileUrl} download={fileName} title="Tải tệp" aria-label={`Tải ${fileName}`}>
      <Download size={17} strokeWidth={2} />
    </a>
  {/if}
</section>

<style>
  .file-preview {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.62rem;
    width: min(100%, 20rem);
    margin-top: 0.45rem;
    padding: 0.58rem 0.62rem;
    border: 1px solid rgba(203, 213, 225, 0.84);
    border-radius: 0.58rem;
    color: #172033;
    background: rgba(248, 250, 252, 0.92);
  }

  .file-icon {
    display: grid;
    place-items: center;
    width: 2.28rem;
    height: 2.28rem;
    border-radius: 0.54rem;
    color: #475569;
    background: #e2e8f0;
  }

  .file-body {
    display: grid;
    min-width: 0;
    gap: 0.14rem;
    color: inherit;
    text-decoration: none;
    text-align: left;
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .file-body strong,
  .file-body span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-body strong {
    font-size: 0.82rem;
    line-height: 1.2;
  }

  .file-body span {
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    color: #64748b;
    font-size: 0.72rem;
  }

  .download-action {
    display: grid;
    place-items: center;
    width: 2.05rem;
    height: 2.05rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.5rem;
    color: #334155;
    background: #fff;
  }

  .download-action:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }
</style>
